import type { RevitOperationProgressSnapshot } from '../api/grpc/operationProgressClient';
import type {
  CellUpdateResult,
  PreparedElementUpdate,
  PreparedProjectTypeUpdate,
  QuickFillSyncProgressPhase,
  SaveChangesOptions,
} from './quickFillRevitTypes';
import { emitProgress } from './quickFillRevitTypes';

export function isProjectOnlyTargetFailure(message: string): boolean {
  const normalized = message.toLowerCase();
  return normalized.includes('no writable project instance/type parameter found') &&
    normalized.includes('quick fill does not edit family documents');
}

export function createProjectOnlyTargetError(update: PreparedProjectTypeUpdate): string {
  return `No writable project instance/type parameter found for '${update.ParameterName}' on '${update.FamilyType}'. Quick Fill does not edit family documents.`;
}

function hasBatchProgressData(data: Record<string, unknown>): boolean {
  return typeof data.successCount === 'number' ||
    typeof data.errorCount === 'number' ||
    (Array.isArray(data.errors) && data.errors.length > 0);
}

function getReportedBatchSuccessCount(
  updatesLength: number,
  data: Record<string, unknown>,
  batchSucceeded: boolean
): number {
  if (typeof data.successCount === 'number') {
    return Math.max(0, Math.min(updatesLength, data.successCount));
  }
  if (!batchSucceeded && typeof data.errorCount === 'number') {
    return Math.max(0, Math.min(updatesLength, updatesLength - data.errorCount));
  }
  return updatesLength;
}

function getBatchFallbackError(
  data: Record<string, unknown>,
  batchError: string | undefined,
  fallback: string
): string {
  return (Array.isArray(data.errors) && typeof data.errors[0] === 'string'
    ? data.errors[0]
    : undefined) || batchError ||
    (typeof data.message === 'string' ? data.message : undefined) || fallback;
}

function getBatchErrors(data: Record<string, unknown>, batchError?: string): string[] {
  return [
    ...(Array.isArray(data.errors)
      ? data.errors.filter((error): error is string => typeof error === 'string')
      : []),
    ...(batchError ? [batchError] : []),
  ];
}

function findMatchingBatchError(errors: string[], identifiers: string[]): string | undefined {
  const normalizedIdentifiers = identifiers
    .map((identifier) => identifier.trim().toLowerCase())
    .filter(Boolean);
  if (normalizedIdentifiers.length === 0) return undefined;
  return errors.find((error) => {
    const normalizedError = error.toLowerCase();
    return normalizedIdentifiers.every((identifier) => normalizedError.includes(identifier));
  });
}

export function buildBatchCellResults(
  updates: PreparedProjectTypeUpdate[],
  batchResult: { success: boolean; error?: string; data?: unknown }
): CellUpdateResult[] {
  const data = (batchResult.data as Record<string, unknown> | undefined) || {};
  if (data.queued === true) {
    return updates.map((update) => ({
      familyType: update.rowLabel,
      parameter: update.ParameterName,
      value: update.Value,
      success: true,
    }));
  }
  if (!batchResult.success && !hasBatchProgressData(data)) {
    const error = batchResult.error || 'Batch update failed';
    return updates.map((update) => ({
      familyType: update.rowLabel,
      parameter: update.ParameterName,
      value: update.Value,
      success: false,
      error,
    }));
  }

  let remainingSuccesses = getReportedBatchSuccessCount(updates.length, data, batchResult.success);
  const fallbackError = getBatchFallbackError(data, batchResult.error, 'Not confirmed by Revit');
  const errors = getBatchErrors(data, batchResult.error);
  return updates.map((update) => {
    const matchedError = findMatchingBatchError(errors, [
      update.FamilyType,
      update.FamilyTypeId ?? '',
      update.ParameterName,
    ]);
    if (matchedError) {
      return {
        familyType: update.rowLabel,
        parameter: update.ParameterName,
        value: update.Value,
        success: false,
        error: matchedError,
      };
    }
    if (remainingSuccesses-- > 0) {
      return {
        familyType: update.rowLabel,
        parameter: update.ParameterName,
        value: update.Value,
        success: true,
      };
    }
    return {
      familyType: update.rowLabel,
      parameter: update.ParameterName,
      value: update.Value,
      success: false,
      error: isProjectOnlyTargetFailure(fallbackError)
        ? createProjectOnlyTargetError(update)
        : fallbackError,
    };
  });
}

export function buildElementBatchCellResults(
  updates: PreparedElementUpdate[],
  batchResult: { success: boolean; error?: string; data?: unknown }
): CellUpdateResult[] {
  const data = (batchResult.data as Record<string, unknown> | undefined) || {};
  if (!batchResult.success && !hasBatchProgressData(data)) {
    const error = batchResult.error || 'Element batch update failed';
    return updates.map((update) => ({
      familyType: update.rowLabel,
      parameter: update.parameterName,
      value: update.value,
      success: false,
      error,
    }));
  }

  let remainingSuccesses = getReportedBatchSuccessCount(updates.length, data, batchResult.success);
  const fallbackError = getBatchFallbackError(data, batchResult.error, 'Not confirmed by Revit');
  const errors = getBatchErrors(data, batchResult.error);
  return updates.map((update) => {
    const matchedError = findMatchingBatchError(errors, [update.elementId, update.parameterName]);
    if (matchedError) {
      return {
        familyType: update.rowLabel,
        parameter: update.parameterName,
        value: update.value,
        success: false,
        error: matchedError,
      };
    }
    if (remainingSuccesses-- > 0) {
      return {
        familyType: update.rowLabel,
        parameter: update.parameterName,
        value: update.value,
        success: true,
      };
    }
    return {
      familyType: update.rowLabel,
      parameter: update.parameterName,
      value: update.value,
      success: false,
      error: fallbackError,
    };
  });
}

function mapHostQuickFillApplyPhase(
  progress: RevitOperationProgressSnapshot
): QuickFillSyncProgressPhase {
  const normalized = `${progress.phase} ${progress.functionName}`.toLowerCase();
  if (progress.resultReady || normalized.includes('completed')) return 'completed';
  if (normalized.includes('instance') || normalized.includes('element')) return 'writing-elements';
  if (normalized.includes('type') || normalized.includes('project')) return 'writing-project-types';
  return 'preparing';
}

export function emitHostQuickFillApplyProgress(
  options: SaveChangesOptions | undefined,
  progress: RevitOperationProgressSnapshot
): void {
  emitProgress(options, {
    operationId: progress.operationId,
    phase: mapHostQuickFillApplyPhase(progress),
    message: progress.message || 'Revit is applying Quick Fill changes',
    current: progress.processed ?? 0,
    total: progress.total ?? 0,
    percent: progress.percent,
    detail: progress.detail,
    functionName: progress.functionName,
    currentItem: progress.currentItem,
    state: progress.state,
    waitReason: progress.waitReason,
    waitElapsedMs: progress.waitElapsedMs,
    activeElapsedMs: progress.activeElapsedMs,
    elapsedMs: progress.elapsedMs,
    etaMs: progress.etaMs,
    cacheStatus: progress.cacheStatus,
    resultReady: progress.resultReady,
    failed: progress.failed,
    error: progress.error,
    items: progress.items,
  });
}
